import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Goal } from '@/entities/goal';
import { getSortedPlannedGoals } from '@/entities/planned-goal/operations';
import { useReportQueryError } from '@/hooks/useReportQueryError';
import { useGoalsByIds, useUpdateGoal } from '@/queries/goal';
import {
  useCreateGoalAtPosition,
  useCreateGoalsAtPosition,
  useDeleteGoalFromPlan,
  usePlannedGoals,
  useReorderGoal,
} from '@/queries/planned-goal';
import { GoalsListDisplay } from './GoalsListDisplay';

interface GoalsListProps {
  planId: string;
  period: string;
  startAtLocal: string;
  focusOnMount?: boolean;
  showPlaceholder?: boolean;
}

interface FocusParams {
  goalId: string;
  cursorPosition?: number;
}

export function GoalsList({
  planId,
  period,
  startAtLocal,
  focusOnMount = false,
  showPlaceholder = false,
}: GoalsListProps) {
  const [focusGoal, setFocusGoal] = useState<FocusParams | undefined>(
    undefined,
  );
  const [isInitialFocusPending, setIsInitialFocusPending] =
    useState(focusOnMount);

  const overrideFocusRef = useRef<FocusParams | null>(null);

  const plannedGoalsQuery = usePlannedGoals(planId);
  const createGoalAtPriority = useCreateGoalAtPosition({
    onOptimisticGoalCreated: (goalId) => {
      if (overrideFocusRef.current) {
        setFocusGoal(overrideFocusRef.current);
        overrideFocusRef.current = null;
        return;
      }
      setFocusGoal({ goalId, cursorPosition: 0 });
    },
  });
  const deleteGoalFromPlan = useDeleteGoalFromPlan();
  const { reorderGoal, debouncedReorderGoal } = useReorderGoal(
    planId,
    period,
    startAtLocal,
  );
  const createGoalsAtPosition = useCreateGoalsAtPosition({
    onOptimisticGoalsCreated: (lastGoalId) => {
      setFocusGoal({ goalId: lastGoalId, cursorPosition: 0 });
    },
  });
  const deletingGoalIds = useRef(new Set<string>());

  const plannedGoals = plannedGoalsQuery.data;
  const { goals, error: goalsError } = useGoalsByIds(
    (plannedGoals ?? []).map((pg) => pg.goalId),
    { source: 'GoalsList', planId },
  );

  useReportQueryError(goalsError, 'GoalsList');

  useEffect(() => {
    if (plannedGoals?.length === 0) {
      createGoalAtPriority.mutate({
        planId,
        goalCreateInput: { title: '' },
        insertAtPosition: 0,
        period,
        startAtLocal,
        source: 'manual',
        trigger: 'autoEmpty',
      });
    }
  }, [plannedGoals, createGoalAtPriority, planId, period, startAtLocal]);

  const sortedGoals = useMemo(() => {
    if (!plannedGoals || !goals) {
      return [];
    }

    return getSortedPlannedGoals(plannedGoals)
      .map((plannedGoal) =>
        goals.find((goal) => goal.id === plannedGoal.goalId),
      )
      .filter((goal): goal is Goal => goal !== undefined);
  }, [plannedGoals, goals]);

  const firstGoalId = sortedGoals[0]?.id;
  const initialFocusGoal =
    focusOnMount && isInitialFocusPending && firstGoalId
      ? { goalId: firstGoalId, cursorPosition: 0 }
      : undefined;
  const effectiveFocusGoal = focusGoal ?? initialFocusGoal;

  const handleGoalFocused = useCallback(
    (goalId: string) => {
      if (!focusOnMount || !isInitialFocusPending) {
        return;
      }
      if (goalId !== firstGoalId) {
        return;
      }
      setIsInitialFocusPending(false);
    },
    [focusOnMount, isInitialFocusPending, firstGoalId],
  );

  const uniqueParentGoalIds = useMemo(() => {
    const ids = sortedGoals
      .map((g) => g.parentGoalId)
      .filter((id): id is string => id !== null);
    return [...new Set(ids)];
  }, [sortedGoals]);

  const { goals: parentGoals } = useGoalsByIds(uniqueParentGoalIds, {
    source: 'GoalsList.parentGoals',
    planId,
  });

  const parentTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!parentGoals) {
      return map;
    }
    for (const goal of parentGoals) {
      map.set(goal.id, goal.title);
    }
    return map;
  }, [parentGoals]);

  const { mutate: updateGoal } = useUpdateGoal();

  const plannedGoalPositions = useMemo(() => {
    const map = new Map<string, number>();
    if (!plannedGoals) {
      return map;
    }
    for (const [index, pg] of getSortedPlannedGoals(plannedGoals).entries()) {
      map.set(pg.goalId, index);
    }
    return map;
  }, [plannedGoals]);

  const handleToggle = useCallback(
    (checked: boolean, goalId: string) => {
      const plannedGoal = plannedGoals?.find((pg) => pg.goalId === goalId);
      const goal = sortedGoals.find((g) => g.id === goalId);
      if (!plannedGoal || !goal) {
        return;
      }
      updateGoal({
        id: goalId,
        updates: { status: checked ? 'done' : 'open' },
        period,
        startAtLocal,
        position: plannedGoalPositions.get(goalId) ?? 0,
        previousStatus: goal.status,
      });
    },
    [
      updateGoal,
      plannedGoals,
      sortedGoals,
      period,
      startAtLocal,
      plannedGoalPositions,
    ],
  );

  const handleInsertGoal = useCallback(
    (
      insertAtPosition: number,
      newGoalTitle: string,
      options?: { parentGoalId?: string; focusCurrentGoalId?: string },
    ) => {
      if (plannedGoals === undefined) {
        return;
      }

      if (options?.focusCurrentGoalId) {
        overrideFocusRef.current = {
          goalId: options.focusCurrentGoalId,
          cursorPosition: 0,
        };
      }

      createGoalAtPriority.mutate({
        planId,
        goalCreateInput: {
          title: newGoalTitle,
          ...(options?.parentGoalId
            ? { parentGoalId: options.parentGoalId }
            : {}),
        },
        insertAtPosition,
        period,
        startAtLocal,
        source: 'manual',
        trigger: 'manualInsert',
      });
    },
    [createGoalAtPriority, plannedGoals, planId, period, startAtLocal],
  );

  const handleBackspaceAtStart = useCallback(
    (goalId: string, currentTitle: string) => {
      if (plannedGoals === undefined) {
        return;
      }

      // Don't delete if it's the only goal in the list
      if (plannedGoals.length <= 1) {
        return;
      }

      if (deletingGoalIds.current.has(goalId)) {
        return;
      }

      // Use sortedGoals (visual order) for index calculation instead of plannedGoals
      const currentIndex = sortedGoals.findIndex((g) => g.id === goalId);

      if (currentIndex === 0) {
        return;
      }

      const previousGoal = sortedGoals[currentIndex - 1];
      const previousPlannedGoal = plannedGoals.find(
        (pg) => pg.goalId === previousGoal?.id,
      );

      if (!previousGoal || !previousPlannedGoal) {
        return;
      }

      if (deletingGoalIds.current.has(previousGoal.id)) {
        return;
      }

      if (previousGoal.title.trim() === '') {
        setFocusGoal({ goalId, cursorPosition: 0 });
        deletingGoalIds.current.add(previousGoal.id);
        deleteGoalFromPlan.mutate(
          {
            goalId: previousGoal.id,
            plannedGoals,
            planId,
            period,
            startAtLocal,
          },
          { onSettled: () => deletingGoalIds.current.delete(previousGoal.id) },
        );
        return;
      }

      if (currentTitle.trim() !== '') {
        updateGoal({
          id: previousGoal.id,
          updates: { title: previousGoal.title + currentTitle },
          period,
          startAtLocal,
          position: plannedGoalPositions.get(previousGoal.id) ?? 0,
          previousStatus: previousGoal.status,
        });
      }

      // Set focus immediately for responsive UX
      setFocusGoal({
        goalId: previousGoal.id,
        cursorPosition: previousGoal.title.length,
      });

      deletingGoalIds.current.add(goalId);
      deleteGoalFromPlan.mutate(
        { goalId, plannedGoals, planId, period, startAtLocal },
        {
          onSettled: () => {
            deletingGoalIds.current.delete(goalId);
          },
        },
      );
    },
    [
      plannedGoals,
      deleteGoalFromPlan,
      sortedGoals,
      planId,
      period,
      startAtLocal,
      updateGoal,
      plannedGoalPositions,
    ],
  );

  const handleDeleteAtEnd = useCallback(
    (goalId: string, currentTitle: string) => {
      if (plannedGoals === undefined) {
        return;
      }
      if (plannedGoals.length <= 1) {
        return;
      }
      if (deletingGoalIds.current.has(goalId)) {
        return;
      }

      const currentIndex = sortedGoals.findIndex((g) => g.id === goalId);
      if (currentIndex === -1 || currentIndex === sortedGoals.length - 1) {
        return;
      }

      const nextGoal = sortedGoals[currentIndex + 1];
      const nextPlannedGoal = plannedGoals.find(
        (pg) => pg.goalId === nextGoal?.id,
      );
      if (!nextGoal || !nextPlannedGoal) {
        return;
      }
      if (deletingGoalIds.current.has(nextGoal.id)) {
        return;
      }

      if (currentTitle.trim() === '') {
        setFocusGoal({ goalId: nextGoal.id, cursorPosition: 0 });
        deletingGoalIds.current.add(goalId);
        deleteGoalFromPlan.mutate(
          { goalId, plannedGoals, planId, period, startAtLocal },
          { onSettled: () => deletingGoalIds.current.delete(goalId) },
        );
        return;
      }

      if (nextGoal.title.trim() === '') {
        setFocusGoal({ goalId, cursorPosition: currentTitle.length });
        deletingGoalIds.current.add(nextGoal.id);
        deleteGoalFromPlan.mutate(
          { goalId: nextGoal.id, plannedGoals, planId, period, startAtLocal },
          { onSettled: () => deletingGoalIds.current.delete(nextGoal.id) },
        );
        return;
      }

      const currentGoal = sortedGoals[currentIndex];
      updateGoal({
        id: goalId,
        updates: { title: currentTitle + nextGoal.title },
        period,
        startAtLocal,
        position: plannedGoalPositions.get(goalId) ?? 0,
        previousStatus: currentGoal?.status ?? 'open',
      });

      setFocusGoal({ goalId, cursorPosition: currentTitle.length });

      deletingGoalIds.current.add(nextGoal.id);
      deleteGoalFromPlan.mutate(
        { goalId: nextGoal.id, plannedGoals, planId, period, startAtLocal },
        { onSettled: () => deletingGoalIds.current.delete(nextGoal.id) },
      );
    },
    [
      plannedGoals,
      deleteGoalFromPlan,
      sortedGoals,
      planId,
      period,
      startAtLocal,
      updateGoal,
      plannedGoalPositions,
    ],
  );

  const handleFocusNext = useCallback(
    (currentGoalId: string, cursorPosition: number) => {
      const currentIndex = sortedGoals.findIndex(
        (goal) => goal.id === currentGoalId,
      );
      const nextGoal = sortedGoals[currentIndex + 1];
      if (nextGoal) {
        setFocusGoal({ goalId: nextGoal.id, cursorPosition });
      }
    },
    [sortedGoals],
  );

  const handleFocusPrevious = useCallback(
    (currentGoalId: string, cursorPosition: number) => {
      const currentIndex = sortedGoals.findIndex(
        (goal) => goal.id === currentGoalId,
      );
      const previousGoal = sortedGoals[currentIndex - 1];
      if (previousGoal) {
        setFocusGoal({ goalId: previousGoal.id, cursorPosition });
      }
    },
    [sortedGoals],
  );

  const handleMoveGoal = useCallback(
    (goalId: string, direction: 'up' | 'down') => {
      const currentIndex = sortedGoals.findIndex((g) => g.id === goalId);
      if (currentIndex === -1) {
        return;
      }
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= sortedGoals.length) {
        return;
      }
      debouncedReorderGoal(goalId, newIndex);
      setFocusGoal((prev) => ({
        goalId,
        cursorPosition: prev?.cursorPosition,
      }));
    },
    [sortedGoals, debouncedReorderGoal],
  );

  const handlePasteGoals = useCallback(
    (insertAtPosition: number, titles: string[], parentGoalId?: string) => {
      if (plannedGoals === undefined) {
        return;
      }
      createGoalsAtPosition.mutate({
        planId,
        goalTitles: titles,
        ...(parentGoalId ? { parentGoalId } : {}),
        insertAtPosition,
        period,
        startAtLocal,
        source: 'manual',
        trigger: 'manualPaste',
      });
    },
    [createGoalsAtPosition, plannedGoals, planId, period, startAtLocal],
  );

  const handleDropGoal = useCallback(
    (goalId: string, newIndex: number) => {
      if (newIndex < 0 || newIndex >= sortedGoals.length) {
        return;
      }
      reorderGoal(goalId, newIndex);
    },
    [sortedGoals.length, reorderGoal],
  );

  const goalsContainerRef = useRef<HTMLDivElement>(null);

  const handleEmptySpaceClick = useCallback(() => {
    const textareas = goalsContainerRef.current?.querySelectorAll('textarea');
    if (textareas && textareas.length > 0) {
      const last = textareas[textareas.length - 1];
      if (!last) {
        return;
      }
      last.focus();
      last.setSelectionRange(last.value.length, last.value.length);
    }
  }, []);

  if (goals === undefined) {
    return <div>Loading...</div>;
  }
  if (goalsError) {
    return <div>Error loading goals</div>;
  }

  return (
    <div ref={goalsContainerRef} className="flex flex-1 flex-col">
      <GoalsListDisplay
        goals={sortedGoals}
        parentTitleMap={parentTitleMap}
        period={period}
        startAtLocal={startAtLocal}
        plannedGoalPositions={plannedGoalPositions}
        showPlaceholder={showPlaceholder}
        onToggle={handleToggle}
        onInsertGoal={handleInsertGoal}
        onBackspaceAtStart={handleBackspaceAtStart}
        onDeleteAtEnd={handleDeleteAtEnd}
        onFocusNext={handleFocusNext}
        onFocusPrevious={handleFocusPrevious}
        onMove={handleMoveGoal}
        onPasteGoals={handlePasteGoals}
        onDropGoal={handleDropGoal}
        onGoalFocused={handleGoalFocused}
        focusGoalId={effectiveFocusGoal?.goalId}
        focusCursorPosition={effectiveFocusGoal?.cursorPosition}
      />
      <div
        data-testid="goals-empty-space"
        className="flex-1 cursor-text"
        onClick={handleEmptySpaceClick}
      />
    </div>
  );
}
